import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import { DrawioContainer } from "~/components/Document/drawioView";
import { TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface DocumentInfo {
  createdAt: string;
  modifiedAt: string;
  modifiedBy?: {
    display_name: string;
  };
}

const EntityRelationship: React.FC = () => {
  const { error: projectMembersError } = useProjectMembers();
  const [currentXml, setCurrentXml] = useState<string | undefined>(undefined);
  const { currentUser, currentProject, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const { selectedId } = useParams<{ selectedId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [isLegacyFormat, setIsLegacyFormat] = useState(false);
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const documentApi = useMemo(() => new DocumentApi(), []);

  useEffect(() => {
    if (projectMembersError) {
      console.error("Project members loading error:", projectMembersError);
    }
  }, [projectMembersError]);

  // ER図を取得
  useEffect(() => {
    const fetchERDiagram = async () => {
      setIsLoading(true);

      if (!currentProject) {
        // プロジェクトが選択されていない場合はホームページにリダイレクト
        setIsLoading(false);
        navigate(`/${currentUser.organization_id}/home`);
        return;
      }

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const docId = selectedId || currentProject.outputs?.["entity-relationship"]?.id;

      if (!docId) {
        setIsLoading(false);
        toast.error(TOAST_MESSAGES.DOCUMENT.ER_DIAGRAM_NOT_FOUND);
        navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
        return;
      }

      setDocumentId(docId);

      const response = await documentApi.get(currentUser.organization_id, currentProject.id, docId);

      if (response !== null) {
        const parsedData = JSON.parse(response.content);

        // 旧バージョンのデータ形式（elements/mermaid）をチェック
        if (parsedData.elements || parsedData.mermaid) {
          setIsLegacyFormat(true);
          setIsLoading(false);
          return;
        }

        if (parsedData.xml) {
          setCurrentXml(parsedData.xml);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } else {
        toast.error(TOAST_MESSAGES.DOCUMENT.ER_DIAGRAM_FETCH_FAILED);
        setIsLoading(false);
        if (currentUser.organization_id && currentProject.id) {
          navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
        } else {
          console.error("Cannot navigate: missing organization_id or project id", {
            organization_id: currentUser.organization_id,
            project_id: currentProject.id,
          });
          toast.error(TOAST_MESSAGES.DOCUMENT.ER_NAVIGATION_ERROR);
        }
      }
    };
    fetchERDiagram();
  }, [currentProject, currentUser, navigate, documentApi, selectedId]);

  // ドキュメント情報を取得
  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (!currentProject || !organization || !documentId) return;

      try {
        const response = await documentApi.get(organization.id, currentProject.id, documentId);

        if (response) {
          if (response.modified_by) {
            setDocumentInfo({
              createdAt: response.created_at || "",
              modifiedAt: response.modified_at || "",
              modifiedBy: response.modified_by,
            });
          } else {
            setDocumentInfo({
              createdAt: response.created_at || "",
              modifiedAt: response.modified_at || "",
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch document info:", error);
      }
    };

    fetchDocumentInfo();
  }, [currentProject, organization, documentId, documentApi]);

  if (isLoading) {
    return (
      <div className={styles["entityRelationship"]}>
        <div className={styles["loadingContainer"]}>
          <div className={styles["spinner"]}></div>
          <p>ER図を読み込んでいます...</p>
        </div>
      </div>
    );
  }

  const handleHistoryClick = () => {
    if (organization.id && currentProject.id) {
      navigate(`/${organization.id}/project/${currentProject.id}/entity-relationship/history`);
    } else {
      console.error("Cannot navigate: missing organization id or project id", {
        organization_id: organization.id,
        project_id: currentProject.id,
      });
      toast.error(TOAST_MESSAGES.DOCUMENT.ER_NAVIGATION_ERROR);
    }
  };

  const handleSave = async (xml: string) => {
    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.SAVE_TARGET_NOT_FOUND);
      return;
    }

    try {
      // APIを使ってサーバーに保存
      const updateData = {
        content: JSON.stringify({ xml: xml }),
      };

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        documentId,
        updateData,
      );

      if (!response) {
        toast.error(TOAST_MESSAGES.DOCUMENT.SAVE_FAILED);
        return;
      }
      const processLockApi = new ProcessLockApi();

      const responseProcessLock = await processLockApi.unlock(
        currentUser.organization_id,
        currentProject.id,
        {
          type: "entity-relationship",
          mode: "edit",
        },
      );
      if (responseProcessLock !== null) {
        setCurrentXml(xml);

        const updatedResponse = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          documentId,
        );

        if (updatedResponse) {
          if (updatedResponse.modified_by) {
            setDocumentInfo({
              createdAt: updatedResponse.created_at || "",
              modifiedAt: updatedResponse.modified_at || "",
              modifiedBy: updatedResponse.modified_by,
            });
          } else {
            setDocumentInfo({
              createdAt: updatedResponse.created_at || "",
              modifiedAt: updatedResponse.modified_at || "",
            });
          }
        }
      } else {
        toast.error(TOAST_MESSAGES.DOCUMENT.UNLOCK_FAILED);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.DOCUMENT.ER_SAVE_FAILED);
      throw error;
    }
  };

  return (
    <div className={styles["entityRelationship"]}>
      {documentInfo && (
        <CommonDocumentHeader
          title="ER図"
          documentType="entity-relationship"
          createdAt={documentInfo.createdAt}
          modifiedAt={documentInfo.modifiedAt}
          {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
          isEditable={false}
          isLoading={isLoading}
          handleHistoryClick={handleHistoryClick}
        />
      )}

      {isLegacyFormat ? (
        <p className={styles["errorMessage"]}>
          このデータは旧形式で作成されており、表示できません。
        </p>
      ) : (
        <div className={styles["diagramContainer"]}>
          <DrawioContainer
            xml={currentXml || ""}
            documentType="entity-relationship"
            documentName={`ER図_${currentProject?.name}`}
            onSave={handleSave}
          />
        </div>
      )}
    </div>
  );
};

export default EntityRelationship;
